'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { PLANNER_HREF } from '../brand';
import { kitchenSceneSvg } from '../drawing/scene';
import { SvgArt } from '../drawing/SvgArt';
import { Icon } from '../shared/Icon';
import { headingLevel, items, SkImage, SkLink, text, type HeadingLevel, type Localized, type SkImageRef } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { doorStyleOr, hexOrUndefined, Italicised } from './homeShared';

/**
 * The home hero from the site mockup: text on the left, a picture of a
 * kitchen on the right.
 *
 * <p>The picture is either an uploaded image (eager, high fetch priority: it is
 * the page's largest paint) or, with no image, the theme's drawn kitchen scene
 * in a door style and colour the operator picks. The drawing is inline SVG, so
 * it costs no image request at all.
 *
 * <p>`proof` figures and the floating `badges` are FACTS about the business
 * (a delivery time, a sample offer): they have no defaults and appear only when
 * the owner writes them.
 *
 * <p>Two columns from 1024 px; stacked, text first, below that.
 */
export interface SpatialKitchenHeroProof {
  figure?: Localized;
  label?: Localized;
}

export interface SpatialKitchenHeroBadge {
  title?: Localized;
  body?: Localized;
  /** A colour chip beside the text, e.g. the colour of the doors in the scene. */
  swatch?: string;
}

export interface SpatialKitchenSceneHeroProps {
  eyebrow?: Localized;
  title?: Localized;
  /** One phrase inside `title` to set in italic. */
  emphasis?: Localized;
  intro?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  secondaryCtaLabel?: Localized;
  secondaryCtaHref?: string;
  proof?: SpatialKitchenHeroProof[];
  /** An uploaded picture. When set, it replaces the drawn scene. */
  image?: SkImageRef;
  sceneStyle?: string;
  sceneColour?: string;
  /** Accessible description of the drawn scene. */
  sceneLabel?: Localized;
  badges?: SpatialKitchenHeroBadge[];
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-shero{background:var(--sk-warm);padding-block:40px 56px}
@media (min-width:769px){.sk-shero{padding-block:56px 72px}}
.sk-shero__grid{display:grid;gap:2.5rem;align-items:center}
@media (min-width:1024px){.sk-shero__grid{grid-template-columns:1.02fr 1fr;gap:56px}}
.sk-shero__text{display:grid;gap:0;min-width:0}
.sk-shero__text .sk-eyebrow{margin:0 0 18px}
.sk-shero__title{font-size:clamp(2.3rem,1.5rem + 3vw,4.1rem)}
.sk-shero__lede{margin-top:22px;max-width:58ch}
.sk-shero__ctas{display:flex;flex-wrap:wrap;gap:12px;margin:32px 0 0}
/* A long authored label wraps inside its button rather than pushing the page wider than a 360 px phone. */
@media (max-width:479px){.sk-shero__ctas .sk-btn{white-space:normal;text-align:center}}
.sk-shero__proof{display:flex;flex-wrap:wrap;gap:1rem 28px;margin:40px 0 0;padding:0;list-style:none}
.sk-shero__proof li{font-size:.88rem;color:var(--sk-muted)}
.sk-shero__figure{display:block;font-family:var(--sk-serif);font-size:1.6rem;font-weight:500;line-height:1.25;color:var(--sk-ink)}
.sk-shero__art{position:relative;border-radius:22px;overflow:hidden;box-shadow:var(--sk-shadow-lg);background:#e9e1d4;aspect-ratio:5/4.3;min-width:0}
.sk-shero__art .sk-art,.sk-shero__art .sk-img{display:block;width:100%;height:100%}
.sk-shero__art .sk-art svg{display:block;width:100%;height:100%}
.sk-shero__badge{position:absolute;display:flex;gap:12px;align-items:center;max-width:calc(100% - 24px);background:var(--sk-white);border-radius:14px;box-shadow:var(--sk-shadow-lg);padding:10px 12px;font-size:.8rem;line-height:1.35;color:var(--sk-muted)}
.sk-shero__badge strong{display:block;font-size:.875rem;color:var(--sk-ink)}
.sk-shero__badge--1{left:12px;bottom:16px}
.sk-shero__badge--2{right:12px;top:12px}
.sk-shero__dot{flex:none;width:28px;height:28px;border-radius:8px;box-shadow:inset 0 0 0 1px rgba(29,33,31,.12)}
@media (min-width:640px){
  .sk-shero__badge{padding:14px 16px;font-size:.85rem}
  .sk-shero__badge strong{font-size:.95rem}
  .sk-shero__badge--1{bottom:28px}
  .sk-shero__badge--2{right:18px;top:20px}
  .sk-shero__dot{width:36px;height:36px;border-radius:10px}
}
`;

export function SpatialKitchenSceneHero(props: SpatialKitchenSceneHeroProps) {
  const level = headingLevel(props.headingLevel, 'h1');
  const Title = level;
  const title = text(props.title, 'Plan your kitchen');
  const emphasis = text(props.emphasis);
  const eyebrow = text(props.eyebrow);
  const intro = text(props.intro);
  const ctaLabel = text(props.ctaLabel, 'Design your kitchen');
  const ctaHref = props.ctaHref || PLANNER_HREF;
  const secondaryCtaLabel = text(props.secondaryCtaLabel);
  const proof = items<SpatialKitchenHeroProof>(props.proof)
    .slice(0, 4)
    .filter((item) => text(item.figure) || text(item.label));
  const badges = items<SpatialKitchenHeroBadge>(props.badges)
    .slice(0, 2)
    .filter((badge) => text(badge.title) || text(badge.body));
  const titleId = useId();
  const artId = useId();

  return (
    <BlockRoot as="section" className="sk sk-shero" aria-labelledby={titleId}>
      <SkBaseStyles />
      <SkStyles id="scene-hero" css={CSS} />
      <div className="sk-container sk-shero__grid">
        <div className="sk-shero__text">
          {eyebrow && <p className="sk-eyebrow">{eyebrow}</p>}
          <Title id={titleId} className="sk-heading sk-shero__title">
            <Italicised title={title} emphasis={emphasis} />
          </Title>
          {intro && <p className="sk-lead sk-shero__lede">{intro}</p>}
          <p className="sk-shero__ctas">
            <SkLink href={ctaHref} className="sk-btn sk-btn--primary sk-btn--lg">
              {ctaLabel}
              <Icon name="arrow-right" size={18} />
            </SkLink>
            {secondaryCtaLabel && props.secondaryCtaHref && (
              <SkLink href={props.secondaryCtaHref} className="sk-btn sk-btn--secondary sk-btn--lg">
                {secondaryCtaLabel}
              </SkLink>
            )}
          </p>
          {proof.length > 0 && (
            <ul className="sk-shero__proof">
              {proof.map((item, index) => (
                <li key={`${text(item.figure)}-${index}`}>
                  {text(item.figure) && <strong className="sk-shero__figure">{text(item.figure)}</strong>}{' '}
                  {text(item.label)}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="sk-shero__art">
          {props.image?.src ? (
            <SkImage image={props.image} priority sizes="(min-width: 1024px) 50vw, 100vw" />
          ) : (
            <SvgArt
              svg={kitchenSceneSvg({
                style: doorStyleOr(props.sceneStyle),
                colour: hexOrUndefined(props.sceneColour),
                label: text(props.sceneLabel, 'Illustration of a kitchen'),
                idPrefix: artId,
              })}
            />
          )}
          {badges.map((badge, index) => {
            const swatch = hexOrUndefined(badge.swatch);
            return (
              <div key={`${text(badge.title)}-${index}`} className={`sk-shero__badge sk-shero__badge--${index + 1}`}>
                {swatch && <span className="sk-shero__dot" style={{ background: swatch }} aria-hidden="true" />}
                <div>
                  {text(badge.title) && <strong>{text(badge.title)}</strong>}{' '}
                  {text(badge.body)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </BlockRoot>
  );
}
